import { useMemo } from 'react'
import { BaseTable } from 'Features/Table/BaseTable.view'
import { ExtendedGridColDef } from 'Features/Table/BulkUpdateModal'
import {
  ProductionRequirementRowType,
  SheduleTableProps,
} from './Scheduler.types'
import { Box } from '@mui/material'
import {
  ProductRequirementsCell,
  RequirementsByProduct,
  WEEK_NUMBERS,
} from './ProductRequirementsCell.view'
//Optional	Not required	Optional	Optional	Optional	Mandatory	Optional	Not required
//conversionFactor	id	inputProductCode	inputProductName	maxRunTime	productCode	productName	quantity
const dataConfig = {
  required: ['productCode', 'conversionFactor'],
  optional: [
    'inputProductCode',
    'inputProductName',
    'maxRunTime',
    'productName',
    'quantity',
    // These four no longer have a column in the grid, but stay listed here on
    // purpose: the fields still exist in the data and still constrain a run
    // (temperature and usage group in the engine's sync/usage-group managers,
    // the other two in the basket/steriliser constraint), so a CSV that
    // carries them must keep loading. Removing the column removed the place to
    // EDIT them, not the data.
    'requiredTemperature',
    'usageGroup',
    'requiresSterilisation',
    'basketFamilyId',
    // The product's bottle format. Optional because the column is nullable:
    // not every product has a format, and a blank one never incurs a
    // changeover. See the grid column below.
    'format',
  ],
}

export const SchedulerProductionMasterDataTable = ({
  data,
  isLoading,
  updateTableData,
  uploadScheduleData,
  isUploading,
  isSaving,
  updateRowsStatus,
  predicate,
  deleteTableData,
  isDeleting,
  unfilteredData,
  requirementsByProduct,
  requirementsMax = 0,
  requirementsLoading = false,
}: SheduleTableProps<ProductionRequirementRowType> & {
  /** Per-week requirement totals per productCode. When present the
   *  Requirements column is shown; `undefined` means the planner has not
   *  asked for it and the eight weekly reads have not run. */
  requirementsByProduct?: RequirementsByProduct
  /** Largest single-week quantity across every product — the shared bar
   *  scale, so bar lengths are comparable between rows. */
  requirementsMax?: number
  requirementsLoading?: boolean
}) => {
  // Handle data updates for BaseTable
  const handleDataUpdate = (
    changedRows: Record<number, ProductionRequirementRowType>
  ) => {
    updateTableData(changedRows)
  }
  //Example data:
  // {
  //   conversionFactor: '8.4'
  //   id: 0
  //   inputProduct: '901626939'
  //   maxQuantity: '17000'
  //   product: '832275'
  //   productName: 'PHUT GARLIC & HERB 336X25G'
  //   quantity: '0'
  // }

  const columns: ExtendedGridColDef[] = useMemo(
    () => [
      {
        headerName: 'Product code',
        field: 'productCode',
        type: 'string',
        flex: 1,
      },
      {
        headerName: 'Product description',
        field: 'productName',
        type: 'string',
        flex: 1,
      },
      {
        headerName: 'Input Product code',
        field: 'inputProductCode',
        type: 'string',
        flex: 1,
      },
      {
        headerName: 'Input Product description',
        field: 'inputProductName',
        type: 'string',
        flex: 1,
      },
      {
        headerName: 'Conversion Factor',
        field: 'conversionFactor',
        type: 'number',
        flex: 1,
        editable: true,
      },
      {
        // The product's bottle format, and the source of truth for the whole
        // format-changeover feature: the Format Changeover tab prices
        // transitions between the values typed here, and matches them by
        // exact spelling. Deliberately free text rather than a select —
        // there is no format master list to pick from, this column IS it.
        headerName: 'Format',
        field: 'format',
        type: 'string',
        flex: 1,
        editable: true,
        description:
          'The bottle format this product runs in — e.g. POUCH, BIB. Leave blank for products that have no format: a blank never incurs a format changeover in either direction. Transitions between the values used here are priced on the Format Changeover tab, matched on exact spelling.',
      },
      // Production requirements, only when the planner has asked for them —
      // the data behind this is eight per-week reads. Read-only: it is a view
      // of the production-requirements tab, and editing a quantity belongs
      // there, per week, where the row actually lives.
      ...(requirementsByProduct
        ? [
            {
              headerName: 'Requirements (wk 1-8)',
              field: '__requirements',
              sortable: true,
              filterable: false,
              editable: false,
              width: 190,
              description:
                "This product's production requirement across weeks 1-8: the total, then one bar per week. Bars share one scale across every product, so lengths are comparable between rows. Click for the week-by-week numbers. Read-only - edit quantities on the Production Requirements tab.",
              // Sorting and CSV export both need a number, not a component.
              valueGetter: (
                _value: unknown,
                row: ProductionRequirementRowType
              ) =>
                (
                  requirementsByProduct.get(String(row?.productCode)) ?? []
                ).reduce((a, b) => a + b, 0),
              renderCell: (params: { row: ProductionRequirementRowType }) => (
                <ProductRequirementsCell
                  productCode={String(params.row?.productCode ?? '')}
                  productName={params.row?.productName}
                  weeks={
                    requirementsByProduct.get(
                      String(params.row?.productCode)
                    ) ?? Array(WEEK_NUMBERS.length).fill(0)
                  }
                  globalMax={requirementsMax || 1}
                  isLoading={requirementsLoading}
                />
              ),
            } as ExtendedGridColDef,
          ]
        : []),
    ],
    [requirementsByProduct, requirementsMax, requirementsLoading]
  )

  return (
    <Box>
      <BaseTable
        data={data ?? []}
        columns={columns}
        isLoading={isLoading}
        updateTableData={handleDataUpdate}
        uploadData={uploadScheduleData}
        isUploading={isUploading}
        isSaving={isSaving}
        checkBoxSelection={true}
        showTableToolBar={true}
        downloadFileName="SchedulerProductionMasterData"
        multiStageUpload={{
          uploadFile: uploadScheduleData,
          isUploading: isUploading,
          columns: columns,
          updateRowsStatus: updateRowsStatus,
          // Unfiltered on purpose — see `unfilteredData` in SheduleTableProps.
          originalData: unfilteredData ?? data,
          predicate: predicate,
          dataConfig: dataConfig,
        }}
        initialState={{
          pagination: { paginationModel: { pageSize: 100 } },
        }}
        sx={{
          maxWidth: 'calc(100vw - 70px)',
          '& .MuiDataGrid-cell--editable': {
            backgroundColor: '#CBD5E1',
          },
          '& .MuiBox-root': {
            padding: '0px !important',
          },
        }}
        onDeleteRows={deleteTableData}
        isDeleting={isDeleting}
      />
    </Box>
  )
}
